import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { BulkOutcome } from "@app/shared";
import { applicationsApi } from "./api";
import {
  clearBulkResult,
  clearSelection,
  loadBulkResult,
  loadSelection,
  saveBulkResult,
  saveSelection,
} from "./bulkSelection";
import { Button, Card, Field, Input, PageTitle } from "../../components/ui";
import { ApiError } from "../../lib/api";

interface Row {
  companyId: string;
  name: string;
  headcount: number;
  desiredJobType: string;
  note: string;
}

const OUTCOME_LABEL: Record<BulkOutcome, { label: string; cls: string }> = {
  created: { label: "申込しました", cls: "text-emerald-600" },
  already_applied: { label: "すでに申込済み", cls: "text-slate-500" },
  unavailable: { label: "受付不可（公開停止など）", cls: "text-amber-600" },
  failed: { label: "送信に失敗（再送信でリトライ）", cls: "text-rose-500" },
};

export function BulkApplyPage() {
  const qc = useQueryClient();
  // 選択と結果は sessionStorage 起点（マウント時に確定・§4.3）。
  const [selection] = useState(loadSelection);
  const nameById = useMemo(() => new Map(selection.map((c) => [c.id, c.name])), [selection]);

  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [targetHeadcount, setTargetHeadcount] = useState(1);
  const [rows, setRows] = useState<Row[]>(() =>
    selection.map((c) => ({
      companyId: c.id,
      name: c.name,
      headcount: 1,
      desiredJobType: c.jobTypes[0] ?? "",
      note: "",
    })),
  );
  const [result, setResult] = useState(loadBulkResult);

  const mutation = useMutation({
    mutationFn: () =>
      applicationsApi.bulk({
        startDate,
        endDate,
        targetHeadcount: Number(targetHeadcount),
        items: rows.map((r) => ({
          companyId: r.companyId,
          headcount: Number(r.headcount),
          desiredJobType: r.desiredJobType,
          note: r.note,
        })),
      }),
    onSuccess: (r) => {
      setResult(r);
      saveBulkResult(r);
      qc.invalidateQueries({ queryKey: ["applications"] });
    },
  });

  const setRow = (i: number, patch: Partial<Row>) =>
    setRows((rs) => rs.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));

  const sum = rows.reduce((s, r) => s + (Number(r.headcount) || 0), 0);
  const over = sum > (Number(targetHeadcount) || 0);
  // 1社でも人数<1 or 希望職種が空だと zod で全体400になるため、送信前に弾く（F4）。
  const rowsValid = rows.every((r) => Number(r.headcount) >= 1 && r.desiredJobType.trim() !== "");
  const apiErr = mutation.error instanceof ApiError ? mutation.error.message : null;

  // ── 結果ビュー（送信後・リロードでも再表示）──
  if (result) {
    return (
      <div className="mx-auto max-w-2xl space-y-4">
        <PageTitle title="一括申込の結果" />
        <Card>
          <p className="text-sm">
            <span className="font-bold text-emerald-600">申込成立 {result.summary.secured} 社</span>
            {" ／ "}
            <span className="font-bold text-rose-500">未申込 {result.summary.notApplied} 社</span>
          </p>
          <ul className="mt-3 divide-y divide-slate-100">
            {result.results.map((r) => (
              <li key={r.companyId} className="flex items-center justify-between py-2 text-sm">
                <span className="text-slate-700">{nameById.get(r.companyId) ?? r.companyId}</span>
                <span className={`font-bold ${OUTCOME_LABEL[r.outcome].cls}`}>
                  {OUTCOME_LABEL[r.outcome].label}
                </span>
              </li>
            ))}
          </ul>
          {result.summary.notApplied > 0 && (
            <p className="mt-3 text-xs text-slate-400">
              未申込（受付不可・失敗）は、選択を保ったまま「入力に戻る」で再送信するとリトライできます（申込済みは重複しません）。
            </p>
          )}
        </Card>
        <div className="flex justify-between">
          <Button
            variant="ghost"
            onClick={() => {
              setResult(null);
              clearBulkResult(); // 結果キーを削除（空オブジェクト保存だとリロードで空の結果が出る・F2）
            }}
          >
            入力に戻る
          </Button>
          <Link to="/app/school/applications">
            <Button>申込一覧へ</Button>
          </Link>
        </div>
      </div>
    );
  }

  // ── 選択が無い（空状態）──
  if (selection.length === 0) {
    return (
      <Card className="mx-auto max-w-xl text-center">
        <PageTitle title="一括申込" />
        <p className="text-sm text-slate-600">
          企業が選択されていません。「企業を探す」で申し込みたい企業にチェックを入れてから、もう一度お越しください。
        </p>
        <div className="mt-6">
          <Link to="/app/school/search">
            <Button>企業を探す</Button>
          </Link>
        </div>
      </Card>
    );
  }

  // ── 入力フォーム ──
  return (
    <div className="mx-auto max-w-3xl space-y-4">
      <PageTitle
        title="一括申込"
        subtitle="選んだ企業に、共通の期間で一度に申し込みます。人数・希望職種・連絡事項は企業ごとに設定できます。"
      />
      <Card className="space-y-4">
        <div className="grid gap-4 md:grid-cols-3">
          <Field label="開始日（全社共通）">
            <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
          </Field>
          <Field label="終了日（全社共通）">
            <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
          </Field>
          <Field
            label="必要な合計受入人数"
            hint="選んだ企業の人数の合計がこれを超えると申し込めません"
          >
            <Input
              type="number"
              min={1}
              value={targetHeadcount}
              onChange={(e) => setTargetHeadcount(Number(e.target.value))}
            />
          </Field>
        </div>
        <p className={`text-sm font-bold ${over ? "text-rose-500" : "text-slate-500"}`}>
          合計 {sum} 人 / 必要 {targetHeadcount} 人
          {over && "（超過しています。人数を減らしてください）"}
        </p>
      </Card>

      <div className="space-y-2">
        {rows.map((r, i) => (
          <Card key={r.companyId} className="space-y-2">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-extrabold text-brand-dark">{r.name}</h3>
              <button
                type="button"
                className="text-xs text-slate-400 hover:text-rose-500"
                onClick={() => {
                  const next = rows.filter((_, idx) => idx !== i);
                  setRows(next);
                  saveSelection(selection.filter((s) => next.some((n) => n.companyId === s.id)));
                }}
              >
                外す
              </button>
            </div>
            <div className="grid gap-3 md:grid-cols-3">
              <Field label="人数">
                <Input
                  type="number"
                  min={1}
                  value={r.headcount}
                  onChange={(e) => setRow(i, { headcount: Number(e.target.value) })}
                />
              </Field>
              <Field label="希望職種">
                <Input
                  value={r.desiredJobType}
                  onChange={(e) => setRow(i, { desiredJobType: e.target.value })}
                />
              </Field>
              <Field label="連絡事項（任意）">
                <Input value={r.note} onChange={(e) => setRow(i, { note: e.target.value })} />
              </Field>
            </div>
          </Card>
        ))}
      </div>

      {apiErr && <p className="text-sm font-bold text-rose-500">{apiErr}</p>}
      <div className="flex justify-between">
        <Button
          variant="ghost"
          onClick={() => {
            clearSelection();
            setRows([]);
          }}
        >
          選択をすべて解除
        </Button>
        <div className="flex gap-2">
          <Link to="/app/school/search">
            <Button variant="ghost">企業を探すに戻る</Button>
          </Link>
          <Button
            disabled={
              mutation.isPending ||
              over ||
              !rowsValid ||
              !startDate ||
              !endDate ||
              rows.length === 0
            }
            onClick={() => mutation.mutate()}
          >
            この内容で一括申込する
          </Button>
        </div>
      </div>
    </div>
  );
}
